import React, { useEffect, useState, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { GET_TRANSACTION_BY_ORGANIZATION_ID } from "../../../../graphql/queries/transaction";
import { Transaction, transactionData } from "./bigchartdata";
import {
  BigChartContainer,
  BigChartWrapper,
  ThirdChart,
  ChartBigData,
  ChartBigPrice,
  ChartBigAmount,
  ChartBigPercent,
  ChartAmountContent,
  ChartInnerDiv,
  ChartFlow,
  ChartFlowContainer,
  ChartFlowContent,
  ChartFlowWrapper,
  ChartFlowDiv,
  Circle,
  Circle01,
  StyledAreaChart,
} from "./bigChart.styles";
import { ChartContent, ChartSpan, ChartData } from "../HomeChart/chart.styles";
import { colors } from "../../../../theme/color";
import { LuCalendarDays } from "react-icons/lu";
import {
  FlyoutMenu,
  CalendarText,
  YearFilter,
  DropDownIcon,
  DropDownMenuList,
  DropDownMenu,
} from "../../../../pages/Dashboard/Dashboard.styles";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import environment from "../../../../env";
import { scaleValue } from "../../../../utils/scaleValue";
import { returnMonthAndYear } from "../../../../utils/formatTimeAndDate";

interface MonthlyData {
  name: string;
  uv: number;
  pv: number;
  amt: number;
  inflow: number;
  outflow: number;
}

const Index = () => {
  const [isDropDown, setIsDropDown] = useState(false);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dataTrans, setDataTrans] = useState<MonthlyData[]>([]);
  const [months, setMonths] = useState<string[]>([]);
  const [years, setYears] = useState<string[]>([]);
  const [minValue, setMinValue] = useState<number>(0);
  const [maxValue, setMaxValue] = useState<number>(20000);
  const [minY, setMinY] = useState<number>(50);
  const [maxY, setMaxY] = useState<number>(300);

  const { loading, error, data } = useQuery(
    GET_TRANSACTION_BY_ORGANIZATION_ID,
    {
      variables: {
        payload: {
          limit: 50,
          page: 1,
        },
      },
    }
  );

  // Memoizing transactions to prevent unnecessary re-renders
  const transactions: Transaction[] = useMemo(
    () => data?.getAllTransactionsByOrganizationId?.data || [],
    [data]
  );

  useEffect(() => {
    if (transactions.length > 0) {
      const monthSet: { [key: string]: boolean } = {};
      const uniqueMonths: string[] = [];

      transactions.forEach((t: Transaction) => {
        const { month } = returnMonthAndYear(t.updatedAt);
        if (!monthSet[month]) {
          monthSet[month] = true;
          uniqueMonths.push(month);
        }
      });

      const allMonths = ["Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

      // Extract unique years
      const yearSet: { [key: string]: boolean } = {};
      const uniqueYears: string[] = [];

      transactions.forEach((t: Transaction) => {
        const { year } = returnMonthAndYear(t.updatedAt);
        if (!yearSet[year]) {
          yearSet[year] = true;
          uniqueYears.push(year);
        }
      });

      setMonths(allMonths);
      setYears(uniqueYears);

      const monthlyData = allMonths.map((month: string) => {
        const monthlyTransactions = transactions.filter(
          (transaction: Transaction) => {
            const transactionDate = returnMonthAndYear(transaction.updatedAt);
            return (
              transactionDate.month === month &&
              transactionDate.year === selectedYear
            );
          }
        );

        const totalUv = monthlyTransactions.reduce(
          (sum, trans) => sum + trans.quantity,
          0
        );
        const totalPv = monthlyTransactions.reduce(
          (sum, trans) => sum + trans.price,
          0
        );
        const totalAmt = monthlyTransactions.reduce(
          (sum, trans) => sum + trans.amount,
          0
        );

        return {
          name: month,
          uv: totalUv,
          pv: totalPv,
          amt: totalAmt,
          inflow: totalAmt,
          outflow: 0,
        };
      });

      if (JSON.stringify(monthlyData) !== JSON.stringify(dataTrans)) {
        setDataTrans(monthlyData);
      }
    }
  }, [transactions, selectedYear, minValue, maxValue, minY, maxY]);

  const handleDropDownMenu = () => {
    setIsDropDown(!isDropDown);
  };

  const handleSelectedIndex = (index: number) => {
    setSelectedIndex(index);
    setSelectedYear(years[index]);
    setIsDropDown(!isDropDown);
  };

  const tickFormatter = (tick: number) => `$ ${tick}`;

  const generateYTicks = () => {
    const ticks: number[] = [];
    for (let tick = minY; tick <= maxY; tick += 50) {
      ticks.push(tick);
    }
    return ticks;
  };

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    setSelectedYear(`${currentYear}`);
  }, []);

  const totalAmount =
    dataTrans?.reduce((sum, amount) => sum + amount?.amt, 0) || 0;

  return (
    <BigChartContainer>
      <BigChartWrapper>
        <ThirdChart>
          <ChartContent>
            <div>
              <ChartSpan>Payment Analytics</ChartSpan>
            </div>
            <div className="calender">
              <FlyoutMenu>
                <YearFilter onClick={handleDropDownMenu}>
                  <LuCalendarDays
                    color={colors.ButtonBackgroundColors.primaryDefaultButton}
                    size={"20px"}
                  />
                  <CalendarText>Year {selectedYear} </CalendarText>
                  <DropDownIcon
                    src="/assets/icons/Drop_Down.png"
                    onClick={handleDropDownMenu}
                  />
                </YearFilter>

                <DropDownMenu isDropDown={isDropDown}>
                  {years.map((year, index) => (
                    <DropDownMenuList
                      selected={selectedIndex === index}
                      key={year}
                      onClick={() => handleSelectedIndex(index)}
                    >
                      {year}
                    </DropDownMenuList>
                  ))}
                </DropDownMenu>
              </FlyoutMenu>
            </div>
          </ChartContent>
          <ChartFlowContent>
            <ChartBigAmount>
              <ChartAmountContent>
                <ChartBigPrice>${totalAmount}</ChartBigPrice>
                {/* <ChartBigPercent>0%</ChartBigPercent> */}
              </ChartAmountContent>
              <ChartInnerDiv>
                Click on any month to view monthly growth
              </ChartInnerDiv>
            </ChartBigAmount>
            <ChartFlowContainer>
              <ChartFlowWrapper>
                <ChartFlowDiv>
                  <ChartFlow>
                    <Circle></Circle>Inflow
                  </ChartFlow>
                </ChartFlowDiv>
                <ChartFlowDiv>
                  <ChartFlow>
                    <Circle01></Circle01>Outflow
                  </ChartFlow>
                </ChartFlowDiv>
              </ChartFlowWrapper>
            </ChartFlowContainer>
          </ChartFlowContent>
        </ThirdChart>
      </BigChartWrapper>
      <ChartData>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart
            data={dataTrans}
            margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="4 4" vertical={false} />
            <defs>
              <linearGradient id="inflowGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#09005B" stopOpacity={0.5} />
                <stop offset="48.25%" stopColor="#F0EFFF" stopOpacity={0.5} />
                <stop offset="103.37%" stopColor="#FFFFFF" stopOpacity={0.5} />
              </linearGradient>
              <linearGradient id="outflowGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#63D465" stopOpacity={0.5} />
                <stop offset="48.25%" stopColor="#FAFAFF" stopOpacity={0.5} />
                <stop offset="103.37%" stopColor="#FFFFFF" stopOpacity={0.5} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#5E5E5E", fontSize: 12 }}
              interval={0}
            />
            <YAxis
              domain={[minY, maxY]}
              ticks={generateYTicks()}
              tickLine={false}
              axisLine={false}
              tickFormatter={tickFormatter}
              tick={{ fill: "#5E5E5E", fontSize: 12 }}
            />
            <Tooltip />
            <Area
              type="monotone"
              dataKey="inflow"
              stroke="#63D465"
              strokeWidth={2}
              fill="url(#inflowGradient)"
            />
            <Area
              type="monotone"
              dataKey="outflow"
              stroke="#09005B"
              strokeWidth={2}
              fill="url(#outflowGradient)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartData>
    </BigChartContainer>
  );
};

export default Index;
